/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useMemo } from 'react';
import { getErrorMessage } from '../../utils/toast';
import { Modal, Form, InputNumber, Input, Select, message, Space, Typography, Divider, Slider, Row, Col } from 'antd';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCompleteEventMutation, useGetEventByIdQuery } from '../../api/plantingEventsApi';
import { useGetEventTemplateByIdQuery } from '../../api/eventTemplatesApi';
import { EventQueueItem, Property, PropertyDataType, CompleteEventPayload } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { Text } = Typography;

const styles = {
  quantitySection: css({ marginTop: 16 }),
};

interface CompleteEventModalProps {
  open: boolean;
  event: EventQueueItem | null;
  farmId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Модалка для завершения события из очереди с опциональными измерениями
 */
export const CompleteEventModal: React.FC<CompleteEventModalProps> = ({
  open,
  event,
  farmId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('events');
  const save = useSave();
  const [form] = Form.useForm();
  const [attributeValues, setAttributeValues] = useState<Record<string, number | string | boolean>>({});
  const [quantityAffected, setQuantityAffected] = useState<number | undefined>(undefined);

  const [completeEvent, { isLoading: completing }] = useCompleteEventMutation();

  // Загружаем полные данные события, включая plantingType и plantingQuantity
  const { data: eventDetail } = useGetEventByIdQuery(
    event?.id && open ? { saveId: save.id, eventId: event.id } : skipToken
  );
  
  // CN-0014: Properties загружаются из шаблона (а не из старых attributes)
  // Для событий без шаблона properties не нужны

  // Загружаем детали шаблона, если событие создано из шаблона
  const { data: templateDetail, isLoading: loadingTemplate } = useGetEventTemplateByIdQuery(
    event?.templateId && open && save?.id ? { id: event.templateId, saveId: save.id } : skipToken
  );



  // Фильтруем атрибуты: показываем ТОЛЬКО если событие создано из шаблона
  const attributes = useMemo(() => {
    // Если есть templateId - ждем загрузки шаблона
    if (event?.templateId) {
      if (loadingTemplate) {
        return []; // Показываем пустой список пока загружается
      }
      if (templateDetail?.properties && templateDetail.properties.length > 0) {
        // Преобразуем properties шаблона в формат Property
        return templateDetail.properties.map((templateProp) => {
          // Маппинг типов данных: decimal → float, number → integer
          let mappedDataType: PropertyDataType;
          switch (templateProp.dataType) {
            case 'number':
              mappedDataType = 'integer';
              break;
            case 'decimal':
              mappedDataType = 'float';
              break;
            case 'text':
            case 'boolean':
              mappedDataType = templateProp.dataType;
              break;
            default:
              mappedDataType = 'text'; // fallback
          }

          return {
            id: templateProp.propertyId,
            code: templateProp.code,
            name: templateProp.name,
            objectType: 'planting' as const,
            dataType: mappedDataType,
            dataTypeCode: mappedDataType === 'integer' ? 0 : mappedDataType === 'text' ? 1 : mappedDataType === 'float' ? 2 : 3,
            unitId: null,
            unit: templateProp.unit, // Добавляем unit из шаблона
            options: templateProp.options?.map(opt => opt.value) ?? [],
            isService: false,
            semanticType: 'measured' as const,
          };
        });
      }
      // Если шаблон загрузился, но атрибутов нет - возвращаем пустой массив
      return [];
    }
    // ИСПРАВЛЕНИЕ CN-0008: Если нет templateId - НЕ показываем атрибуты
    // Событие без шаблона завершается без заполнения атрибутов
    return [];
  }, [event?.templateId, templateDetail, loadingTemplate]);

  // Сбросить форму при открытии/закрытии
  useEffect(() => {
    if (open && event && eventDetail) {
      form.resetFields();
      setAttributeValues({});
      // Устанавливаем значение по умолчанию для групповых посадок
      if (eventDetail.plantingType === 'batch') {
        // По умолчанию - полное выполнение (максимальное значение)
        setQuantityAffected(eventDetail.plantingQuantity);
      } else {
        setQuantityAffected(undefined);
      }
    }
  }, [open, event, eventDetail, form]);

  const handleSubmit = async () => {
    if (!event || !eventDetail) return;

    try {
      // Валидация для групповых посадок (CN-0007)
      if (eventDetail.plantingType === 'batch' && quantityAffected !== undefined) {
        const currentCompleted = eventDetail.quantityAffected || 0;
        const minAllowed = currentCompleted + 1;
        
        if (quantityAffected < minAllowed) {
          message.error(t('complete.minQuantity', { min: minAllowed, done: currentCompleted }));
          return;
        }
        if (quantityAffected > eventDetail.plantingQuantity) {
          message.error(t('quantity.maxUnits', { count: eventDetail.plantingQuantity }));
          return;
        }
      }
      
      // Формируем payload (properties в объектном формате {"26": 215})
      const payload: CompleteEventPayload = {};
      
      if (Object.keys(attributeValues).length > 0) {
        payload.properties = attributeValues;
      }
      
      // Добавляем quantity_affected для групповых посадок
      if (eventDetail.plantingType === 'batch' && quantityAffected !== undefined) {
        payload.quantity_affected = quantityAffected;
      }

      const result = await completeEvent({
        saveId: save.id,
        eventId: event.id,
        payload,
      }).unwrap();
      
      // Обработка частичного выполнения для batch plantings
      if (result.partialCompletion) {
        const { quantityAffected, totalQuantity, isFullyCompleted } = result.partialCompletion;
        if (isFullyCompleted) {
          message.success(
            result.updatedProperties > 0
              ? t('complete.fullyDoneWithProps', { done: quantityAffected, total: totalQuantity, props: result.updatedProperties })
              : t('complete.fullyDone', { done: quantityAffected, total: totalQuantity })
          );
        } else {
          message.info(
            result.updatedProperties > 0
              ? t('complete.partialWithProps', { done: quantityAffected, total: totalQuantity, props: result.updatedProperties })
              : t('complete.partial', { done: quantityAffected, total: totalQuantity })
          );
        }
      } else {
        // Обычное завершение для single plantings
        message.success(
          result.updatedProperties > 0
            ? t('complete.doneWithProps', { props: result.updatedProperties })
            : t('complete.done')
        );
      }
      
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Failed to complete event:', err);
      message.error(err?.data?.message ? getErrorMessage(err) : t('complete.failed'));
    }
  };

  const handleAttributeChange = (attrId: number, value: number | string | boolean | null) => {
    if (value === null || value === undefined || value === '') {
      // Удаляем атрибут из объекта
      const newValues = { ...attributeValues };
      delete newValues[String(attrId)];
      setAttributeValues(newValues);
    } else {
      // Добавляем/обновляем атрибут
      setAttributeValues({
        ...attributeValues,
        [String(attrId)]: value,
      });
    }
  };

  const renderAttributeInput = (attr: Property) => {
    const value = attributeValues[String(attr.id)];

    switch (attr.dataType) {
      case 'integer':
        return (
          <InputNumber
            css={commonStyles.fullWidth}
            placeholder={t('createEvent.enterField', { name: attr.name.toLowerCase() })}
            value={value as number}
            onChange={(val) => handleAttributeChange(attr.id, val)}
            addonAfter={attr.unit || undefined}
          />
        );

      case 'float':
        return (
          <InputNumber
            css={commonStyles.fullWidth}
            placeholder={t('createEvent.enterField', { name: attr.name.toLowerCase() })}
            value={value as number}
            onChange={(val) => handleAttributeChange(attr.id, val)}
            step={0.1}
            precision={2}
            addonAfter={attr.unit || undefined}
          />
        );

      case 'text':
        if (attr.options && attr.options.length > 0) {
          // Если есть опции - показываем Select
          return (
            <Select
              css={commonStyles.fullWidth}
              placeholder={t('createEvent.selectField', { name: attr.name.toLowerCase() })}
              value={value as string}
              onChange={(val) => handleAttributeChange(attr.id, val)}
              allowClear
            >
              {attr.options.map((opt) => (
                <Option key={opt} value={opt}>
                  {opt}
                </Option>
              ))}
            </Select>
          );
        }
        // Иначе - обычный Input
        return (
          <Input
            placeholder={t('createEvent.enterField', { name: attr.name.toLowerCase() })}
            value={value as string}
            onChange={(e) => handleAttributeChange(attr.id, e.target.value)}
          />
        );

      case 'boolean':
        return (
          <Select
            css={commonStyles.fullWidth}
            placeholder={t('propertyForm.selectValue')}
            value={value as boolean}
            onChange={(val) => handleAttributeChange(attr.id, val)}
            allowClear
          >
            <Option value={true}>{t('common:actions.yes')}</Option>
            <Option value={false}>{t('common:actions.no')}</Option>
          </Select>
        );

      default:
        return null;
    }
  };

  if (!event) return null;

  return (
    <Modal
      title={t('complete.title', { id: event.id })}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t('actions.complete')}
      cancelText={t('common:actions.cancel')}
      width={600}
      confirmLoading={completing}
    >
      <Space direction="vertical" css={commonStyles.fullWidth} size="large">
        {/* Информация о событии */}
        <div>
          <Text strong>{t('complete.crop')}</Text> {event.cropName} <Text type="secondary">({event.cropLatinName})</Text>
          <br />
          <Text strong>{t('complete.type')}</Text> {event.type}
          <br />
          <Text strong>{t('complete.plannedDate')}</Text> {event.plannedDate}
          {event.notes && (
            <>
              <br />
              <Text strong>{t('complete.notes')}</Text> <Text type="secondary">{event.notes}</Text>
            </>
          )}
        </div>

        {/* Количество для групповых посадок (CN-0007) */}
        {eventDetail && eventDetail.plantingType === 'batch' && (() => {
          const currentCompleted = eventDetail.quantityAffected || 0;
          const totalQuantity = eventDetail.plantingQuantity;
          const minValue = currentCompleted + 1;
          const maxValue = totalQuantity;
          
          return (
            <>
              <Divider css={css({ margin: '8px 0' })} />
              <div>
                <Text strong>{t('complete.plantsAffected')}</Text>
                <br />
                <Text type="secondary" css={commonStyles.smallText}>
                  {t('complete.batchProgress', { total: totalQuantity, done: currentCompleted })}
                </Text>
                <Row gutter={16} css={styles.quantitySection}>
                  <Col span={16}>
                    <Slider
                      min={minValue}
                      max={maxValue}
                      step={1}
                      value={quantityAffected}
                      onChange={(val) => setQuantityAffected(val)}
                      marks={{
                        [minValue]: `${minValue}`,
                        [maxValue]: `${maxValue}`,
                      }}
                      tooltip={{
                        formatter: (value) => t('complete.pieces', { count: value ?? 0 }),
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      min={minValue}
                      max={maxValue}
                      step={1}
                      value={quantityAffected}
                      onChange={(val) => setQuantityAffected(val ?? minValue)}
                      css={commonStyles.fullWidth}
                      addonAfter={t('complete.piecesShort')}
                    />
                  </Col>
                </Row>
              </div>
            </>
          );
        })()}

        <Divider css={css({ margin: '8px 0' })} />

        {/* Опциональные измерения */}
        <div>
          <Text strong>{t('complete.measurementsOptional')}</Text>
          <br />
          <Text type="secondary" css={commonStyles.smallText}>
            {t('complete.measurementsHint')}
          </Text>
        </div>

        {loadingTemplate ? (
          <Text type="secondary">{t('complete.loadingTemplateAttributes')}</Text>
        ) : attributes.length === 0 ? (
          <Text type="secondary">
            {event?.templateId 
              ? t('complete.noAttributes')
              : t('complete.noTemplate')}
          </Text>
        ) : (
          <Form form={form} layout="vertical">
            {attributes.map((attr) => (
                <Form.Item
                  key={attr.id}
                  label={attr.name}
                  help={attr.code}
                >
                  {renderAttributeInput(attr)}
                </Form.Item>
              ))}
          </Form>
        )}
      </Space>
    </Modal>
  );
};
